import { Capacitor } from '@capacitor/core';
import {
  VideoRecorder,
  VideoRecorderCamera,
  VideoRecorderQuality,
} from '@capacitor-community/video-recorder';

export const VERIFICATION_PHRASE = 'Pineapple Lifestyle';

const WEB_MIME_TYPES = [
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

function getSupportedMimeType(): string {
  for (const type of WEB_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  return 'video/webm';
}

export function useVerificationVideoRecorder() {
  const isNative = Capacitor.isNativePlatform();

  let mediaStream: MediaStream | null = null;
  let mediaRecorder: MediaRecorder | null = null;
  let recordedChunks: Blob[] = [];
  let webFacingMode: 'user' | 'environment' = 'user';

  async function initializePreview(): Promise<void> {
    if (isNative) {
      await VideoRecorder.initialize({
        camera: VideoRecorderCamera.FRONT,
        quality: VideoRecorderQuality.MAX_720P,
        previewFrames: [{
          id: 'verification-recorder',
          stackPosition: 'back',
          width: 'fill',
          height: 'fill',
          x: 0,
          y: 0,
          borderRadius: 0,
          mirrorFrontCam: true,
        }],
      });
      return;
    }

    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: webFacingMode,
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: true,
    });
  }

  async function attachToVideoElement(videoEl: HTMLVideoElement): Promise<void> {
    if (isNative || !mediaStream) {
      return;
    }

    videoEl.srcObject = mediaStream;
    videoEl.muted = true;
    videoEl.playsInline = true;
    await videoEl.play();
  }

  async function startRecording(): Promise<void> {
    if (isNative) {
      await VideoRecorder.startRecording();
      return;
    }

    if (!mediaStream) {
      throw new Error('Camera is not ready.');
    }

    recordedChunks = [];
    mediaRecorder = new MediaRecorder(mediaStream, { mimeType: getSupportedMimeType() });
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunks.push(event.data);
      }
    };
    mediaRecorder.start(250);
  }

  async function stopRecording(): Promise<Blob> {
    if (isNative) {
      const result = await VideoRecorder.stopRecording();
      const response = await fetch(Capacitor.convertFileSrc(result.videoUrl));
      return await response.blob();
    }

    if (!mediaRecorder) {
      throw new Error('Recording has not started.');
    }

    return await new Promise((resolve, reject) => {
      mediaRecorder!.onstop = () => {
        resolve(new Blob(recordedChunks, {
          type: mediaRecorder?.mimeType || getSupportedMimeType(),
        }));
      };
      mediaRecorder!.onerror = () => reject(new Error('Recording failed.'));
      mediaRecorder!.stop();
    });
  }

  async function flipCamera(): Promise<void> {
    if (isNative) {
      await VideoRecorder.flipCamera();
      return;
    }

    webFacingMode = webFacingMode === 'user' ? 'environment' : 'user';
    mediaStream?.getTracks().forEach((track) => track.stop());
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: webFacingMode,
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: true,
    });
  }

  async function destroy(): Promise<void> {
    if (isNative) {
      try {
        await VideoRecorder.destroy();
      } catch {
        // Camera may already be destroyed.
      }
    }

    mediaRecorder = null;
    recordedChunks = [];
    mediaStream?.getTracks().forEach((track) => track.stop());
    mediaStream = null;
  }

  return {
    isNative,
    initializePreview,
    attachToVideoElement,
    startRecording,
    stopRecording,
    flipCamera,
    destroy,
  };
}
